import React, { useState, useEffect, useRef } from 'react';
import { seoServices } from '../../../services/seoAPIServices'; // Adjust path based on your folder structure
import { Input } from '../../ui/input';
import { Loader2, MapPin } from 'lucide-react';

const LocationSearch = ({ onSelect, countryCode, placeholder = "Search City..." }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  const wrapperRef = useRef(null);

  // Click Outside Handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowResults(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  // Search Logic
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        // Use the service we defined
        const data = await seoServices.searchLocations(query, countryCode);
        setResults(data);
        setShowResults(true);
      } catch (error) {
        console.error("Location search failed", error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query, countryCode]);

  const handleSelect = (loc) => {
    setQuery(`${loc.city}, ${loc.state}`); 
    setShowResults(false);
    onSelect(loc);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative">
        <Input 
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
          placeholder={placeholder}
          className="pl-9 h-9 text-sm" // Matching dense UI
        />
        <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        {loading && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" />}
      </div>

      {showResults && results.length > 0 && (
        <ul className="absolute z-50 w-full bg-white border rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
          {results.map((loc) => (
            <li 
              key={loc.id}
              onClick={() => handleSelect(loc)}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm flex flex-col border-b last:border-none"
            >
              <span className="font-medium text-gray-800">{loc.city}</span>
              <span className="text-xs text-gray-500">
                {loc.state}, {loc.country} 
                {loc.population && <span className="ml-1 text-gray-400">• Pop: {(loc.population/1000).toFixed(0)}k</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      
      {showResults && results.length === 0 && query.length > 2 && !loading && (
         <div className="absolute z-50 w-full bg-white border rounded-md shadow-lg mt-1 p-3 text-xs text-gray-500 text-center">
            No locations found.
         </div>
      )}
    </div>
  );
};

export default LocationSearch;